import { useMemo } from 'react';

import {
  InternalNotificationItem,
  useInternalNotifications,
} from './internal-notifications-store';
import { createTimer } from './timer';

type NotificationItem = Omit<
  InternalNotificationItem,
  'id' | 'isOpen' | 'timer'
>;

// Custom hooks for adding/removing notifications
export const useNotifications = () => {
  const { register, unregister, setVisible, setInvisible } =
    useInternalNotifications();

  return useMemo(() => {
    const dismissNotification = (id: string) => {
      // Hide the target notification first (`opacity-100` -> `opacity-0`)
      setInvisible(id);

      // ...and remove it completely one second later.
      createTimer(() => {
        const nextId = unregister(id);

        // If there is a next notification to be displayed, show it after 50 ms (`opacity-0` -> `opacity-100`)
        if (nextId) {
          createTimer(() => setVisible(nextId), 50).resume();
        }
      }, 1000).resume();
    };

    const addNotification = (notification: NotificationItem) => {
      // Register a new notification along with the action to be performed when the time is up.
      register(notification, dismissNotification);
    };

    return { addNotification, dismissNotification };
  }, [register, unregister, setVisible, setInvisible]);
};
